/**
 * Pure request/response mapping for the "one ledger" text-translate path:
 * the baba-v2 mobile gateway's ROOT-mounted `POST /translate` (NOT the
 * `/api/v1/*` surface `../gateway.ts` targets — see `client.ts` for why this
 * reuses that module's base-URL derivation the same way
 * `src/components/chat/gatewayChat.ts`'s `CHAT_WORKER_ORIGIN` does).
 *
 * Contract source: baba-v2 `server/translate/index.ts` (`TranslationRequestBody`,
 * the SSE frames, and the `done`/error payloads) and
 * `mobile/services/translationService.ts` (how the phone fills the same
 * body) — see the doc comments below for the specific line-level behavior
 * this mirrors.
 *
 * No runtime import of `../baba-api` on purpose (only `import type`, which is
 * erased): `../baba-api.ts` will import `./client.ts` (which imports this
 * file) to route `translateStream` through the gateway, so a runtime import
 * back into `../baba-api` would be a real circular dependency. `gateway.ts`
 * documents the same self-contained-copy tradeoff for its own base-URL
 * logic; the small language-name table below follows that precedent instead
 * of importing `LANGUAGES` from `../baba-api`.
 */
import type { GenderId, LangCode, ModeId, ModelId } from '../baba-api'

export interface GatewayLanguageNames {
  name: string
  nativeName: string
}

/** Mirrors `LANGUAGES` in `../baba-api.ts` — see the file doc comment for why
 *  this is a local copy rather than an import. Keep in sync if a language is
 *  added there. */
const LANGUAGE_NAMES: Record<LangCode, GatewayLanguageNames> = {
  he: { name: 'Hebrew', nativeName: 'עברית' },
  en: { name: 'English', nativeName: 'English' },
  fr: { name: 'French', nativeName: 'Français' },
  ru: { name: 'Russian', nativeName: 'Русский' },
  uk: { name: 'Ukrainian', nativeName: 'Українська' },
  es: { name: 'Spanish', nativeName: 'Español' },
  'es-mx': { name: 'Spanish (MX)', nativeName: 'Español (MX)' },
  ar: { name: 'Arabic', nativeName: 'العربية' },
  de: { name: 'German', nativeName: 'Deutsch' },
  it: { name: 'Italian', nativeName: 'Italiano' },
  th: { name: 'Thai', nativeName: 'ไทย' },
  sv: { name: 'Swedish', nativeName: 'Svenska' },
  zh: { name: 'Chinese', nativeName: '中文' },
  ja: { name: 'Japanese', nativeName: '日本語' },
}

export function gatewayLanguageNames(code: LangCode): GatewayLanguageNames {
  return LANGUAGE_NAMES[code] ?? { name: code, nativeName: code }
}

/**
 * The web's own addressee tokens (`GenderId`'s `to_*` values, see
 * `GENDERS` in `../baba-api.ts`) are NOT understood by the gateway's
 * `/translate` prompt builder — only by its separate `/chat` surface (see
 * `server/chat/prompt.ts` in baba-v2). `/translate`'s
 * `server/translate/utils/audience.ts#mapGenderContextToAudience` only
 * recognizes `speaking-to-man` | `speaking-to-woman` | `speaking-to-men-group`
 * | `speaking-to-women-group` | `general` | `personal` — anything else
 * (including the web's `to_male` etc.) silently falls through to "general",
 * silently dropping the user's addressee choice. This maps the web's tokens
 * onto the gateway's `/translate` vocabulary so that choice survives the
 * gateway hop. `to_mixed_group` has no direct equivalent on `/translate`
 * (unlike `/chat`, which does support it) — mapped to the men's-group form,
 * matching the SAME fallback the apps/api path already uses today
 * (`mapGenderContext` in `../baba-api.ts`).
 */
const GENDER_CONTEXT_TO_GATEWAY: Partial<Record<GenderId, string>> = {
  general: 'general',
  to_male: 'speaking-to-man',
  to_female: 'speaking-to-woman',
  to_male_group: 'speaking-to-men-group',
  to_female_group: 'speaking-to-women-group',
  to_mixed_group: 'speaking-to-men-group',
}

export function mapGenderContextToGateway(genderContext: string | undefined): string | undefined {
  if (!genderContext) return undefined
  return GENDER_CONTEXT_TO_GATEWAY[genderContext as GenderId] ?? 'general'
}

/** `gender` (the SPEAKER's own gender) is a separate field from
 *  `genderContext` (the addressee) and the gateway accepts it verbatim as
 *  `'male' | 'female'` — only coerce away non-binary/`general` picks, which
 *  the speaker-gender UI (`SPEAKER_GENDERS` in `../baba-api.ts`) never
 *  actually offers. */
function mapSpeakerGender(gender: GenderId | undefined): 'male' | 'female' | undefined {
  if (gender === 'male' || gender === 'female') return gender
  return undefined
}

export interface GatewayTranslateBodyInput {
  text: string
  sourceLang: LangCode
  targetLang: LangCode
  gender?: GenderId
  genderContext?: string
  mode?: ModeId
  model?: ModelId
  context?: string
  style?: string
  /** Opaque id of the completed gateway translation whose audience the user
   *  is changing. The Worker resolves the text from its own job store. */
  audienceChangeFromId?: string
  /** Fresh per request (`crypto.randomUUID()`) — required so `POST
   *  /translate/cancel` can target this exact job. */
  jobId: string
}

/** Wire shape of baba-v2 `TranslationRequestBody` (server/translate/index.ts)
 *  that this builds. Fields the gateway does not use for a web caller
 *  (`stableUserId`, `regeneratedFrom`, `previousTranslations`,
 *  `shouldSendPushOnComplete`) are intentionally omitted rather than sent as
 *  `undefined` — the gateway resolves the caller's stable id from the
 *  bearer token itself for a `baba_web` issuer. */
export interface GatewayTranslateRequestBody {
  text: string
  direction: 'other-to-he' | 'he-to-other'
  language: string
  languageNative: string
  sourceLanguage: string
  sourceLanguageNative: string
  targetLanguage: string
  targetLanguageNative: string
  model?: string
  mode: 'standard' | 'summary'
  gender?: 'male' | 'female'
  genderContext?: string
  context?: string
  style?: string
  audienceChangeFromId?: string
  jobId: string
  isAppActive: boolean
  platform: 'web'
}

/**
 * Builds the gateway's `/translate` request body from the web's
 * `TranslateOptions`-shaped fields. The web (like the phone) only ever
 * pairs Hebrew with one other language per translation — see
 * `setSourceLangSafe`/`setTargetLangSafe` in `Translator.tsx`, which keep
 * exactly one side pinned to `'he'` — so `direction` and the "non-Hebrew
 * language" fields fall out of whichever side is `'he'`.
 *
 * `mode` only carries `'standard' | 'summary'` through: the web's `'slang'`
 * mode has no `/translate` server-side equivalent (slang mode was removed
 * from that handler — see its `resolvedMode` comment) and is coerced to
 * `'standard'`, matching what the server itself does for any unrecognized
 * mode value.
 */
export function buildGatewayTranslateBody(input: GatewayTranslateBodyInput): GatewayTranslateRequestBody {
  const isHeToOther = input.sourceLang === 'he'
  const direction: 'other-to-he' | 'he-to-other' = isHeToOther ? 'he-to-other' : 'other-to-he'
  const otherLang = isHeToOther ? input.targetLang : input.sourceLang

  const source = gatewayLanguageNames(input.sourceLang)
  const target = gatewayLanguageNames(input.targetLang)
  const other = gatewayLanguageNames(otherLang)

  const body: GatewayTranslateRequestBody = {
    text: input.text,
    direction,
    language: other.name,
    languageNative: other.nativeName,
    sourceLanguage: source.name,
    sourceLanguageNative: source.nativeName,
    targetLanguage: target.name,
    targetLanguageNative: target.nativeName,
    mode: input.mode === 'summary' ? 'summary' : 'standard',
    jobId: input.jobId,
    isAppActive: true,
    platform: 'web',
  }

  if (input.model) body.model = input.model
  const speakerGender = mapSpeakerGender(input.gender)
  if (speakerGender) body.gender = speakerGender
  const genderContext = mapGenderContextToGateway(input.genderContext)
  if (genderContext) body.genderContext = genderContext
  if (input.context) body.context = input.context
  if (input.style) body.style = input.style
  if (input.audienceChangeFromId) {
    body.audienceChangeFromId = input.audienceChangeFromId
  }

  return body
}

// ---- SSE response parsing ----
//
// The gateway's stream (see `handleTranslation` in `server/translate/index.ts`)
// sends `event: start\ndata: {}\n\n` first, then zero or more
// `data: {"delta","fullText"}\n\n` frames, then exactly one terminal
// `data: {"done":true,...}\n\n` or `data: {"error":"..."}\n\n` frame. Every
// frame's payload is self-describing (has `error`, or `done === true`, or a
// string `delta`, or is the empty `{}` paired with the `event: start` line),
// so a single `data: ` line is enough to classify it without also tracking
// the preceding `event: ` line — this mirrors how the web's existing apps/api
// SSE parser (`translateStream` in `../baba-api.ts`) already only looks at
// `data: ` lines.

export interface GatewaySseDeltaEvent {
  type: 'delta'
  delta: string
  fullText: string
}

export interface GatewaySseDoneEvent {
  type: 'done'
  translation: string
  transliteration: string
  charCount: number
  isOverLimit: boolean
  isPro: boolean
  updatedUsage: number
  periodLimit: number
  /** The gateway's `translations` row id for this completion — only present
   *  when the request carried a `jobId` (it always does here). */
  historyId?: string
}

export interface GatewaySseErrorEvent {
  type: 'error'
  message: string
}

export interface GatewaySseStartEvent {
  type: 'start'
}

export type GatewaySseEvent = GatewaySseStartEvent | GatewaySseDeltaEvent | GatewaySseDoneEvent | GatewaySseErrorEvent

/** Parses one raw line from the gateway's SSE body. Returns `null` for a
 *  non-`data:` line (e.g. the `event: start` line, or a blank frame
 *  separator) or a `data:` line whose payload isn't valid/recognized JSON. */
export function parseGatewaySseLine(line: string): GatewaySseEvent | null {
  if (!line.startsWith('data: ')) return null
  const raw = line.slice(6).trim()
  if (!raw) return null

  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return null
  }
  if (!data || typeof data !== 'object') return null
  const obj = data as Record<string, unknown>

  if (typeof obj.error === 'string') {
    return { type: 'error', message: obj.error }
  }
  if (obj.done === true) {
    return {
      type: 'done',
      translation: typeof obj.translation === 'string' ? obj.translation : '',
      transliteration: typeof obj.transliteration === 'string' ? obj.transliteration : '',
      charCount: typeof obj.charCount === 'number' ? obj.charCount : 0,
      isOverLimit: obj.isOverLimit === true,
      isPro: obj.isPro === true,
      updatedUsage: typeof obj.updatedUsage === 'number' ? obj.updatedUsage : 0,
      periodLimit: typeof obj.periodLimit === 'number' ? obj.periodLimit : 0,
      historyId: typeof obj.historyId === 'string' ? obj.historyId : undefined,
    }
  }
  if (typeof obj.delta === 'string') {
    return {
      type: 'delta',
      delta: obj.delta,
      fullText: typeof obj.fullText === 'string' ? obj.fullText : obj.delta,
    }
  }
  // The `data: {}` frame paired with `event: start` — no other shape reaches here.
  return { type: 'start' }
}
